
import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Loader2, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Product } from "../types";
import { useLocation } from "@/contexts/LocationContext";
import { StockWriteLocationItems } from "@/features/locations/components/StockWriteLocationItems";
import { useOrganization } from "@/contexts/OrganizationContext";
import { trpc } from "@/lib/trpc";
import {
  ClientTargetPicker,
  ClientTargetChip,
} from "./ClientTargetPicker";
import { useConnectedClients } from "../hooks/useConnectedClients";
import { useReceiveStockUndo } from "../hooks/useReceiveStockUndo";
import { AdjustmentReferenceField } from "./AdjustmentReferenceField";
import { useTargetCatalog } from "../hooks/useTargetCatalog";
import { joinParts } from "../utils/joinParts";
import { filterToTargetOrg } from "../utils/filterToTargetOrg";

const SHARED_LOCATIONS_ENABLED =
  (import.meta.env.VITE_SHARED_LOCATIONS_ENABLED ?? "") === "true";

/**
 * BLU-3xx Phase 47.01.2-B-04: result shape for the add-stock callback. The
 * modal needs `transactionId` so it can wire the 30-second Undo toast (sonner)
 * to `inventory.reverseReceipt`. Single-tenant callers can return
 * `{ success: true }` without a transactionId — the toast simply skips.
 */
export type AddStockResult =
  | { success: true; transactionId?: string }
  | { success: false };

interface AddStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * BLU-3xx Phase 47.01.2-B-04: extended signature carries an optional
   * `targetOrganizationId` (own-org or a connected client at a shared
   * 3PL location) and returns `{ success, transactionId? }` so the modal
   * can show the Undo toast.
   *
   * BLU-3xx Phase 47.01.2-B-fix3.1: takes the resolved `Product` object,
   * not a sku string. The modal already has the resolved product (via
   * `useTargetCatalog` for cross-org or `products` prop for own-org), so
   * the parent can pass `product.id` directly to the mutation without
   * a string→object lookup that would fail for cross-org SKUs.
   */
  onAddStock: (
    product: Product,
    quantity: number,
    date: Date,
    locationId?: string,
    targetOrganizationId?: string,
    /** Optional operator reference / order number (Thread Lab stock log). */
    reference?: string,
  ) => Promise<AddStockResult | void> | AddStockResult | void;
  products: Product[];
}

const AddStockModal: React.FC<AddStockModalProps> = ({
  isOpen,
  onClose,
  onAddStock,
  products,
}) => {
  // `allLocations`: receiving stock is a WRITE, and stock genuinely sits at
  // delegated locations. `StockWriteLocationItems` sorts those last and marks
  // them "(fulfilled elsewhere)" so choosing one is deliberate.
  const { selectedLocation, allLocations } = useLocation();
  const { organization } = useOrganization();
  const showReceiveUndo = useReceiveStockUndo();
  const { t } = useTranslation('inventory');
  const [selectedSku, setSelectedSku] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [date, setDate] = useState<Date>(new Date());
  // Optional operator reference / order number — creates the Order/Ref link
  // in the stock log when filled; a plain unreferenced adjustment when not.
  const [reference, setReference] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [targetLocationId, setTargetLocationId] = useState<string>("");
  // BLU-3xx Phase 47.01.2-B-04: cross-org target. Required at shared locations,
  // hidden elsewhere. No default — operator MUST pick.
  const [targetOrgId, setTargetOrgId] = useState<string | null>(null);
  // BLU-3xx Phase 47.01.2-B-fix3.3: in-flight indicator. Mirrors
  // UnavailableAdjustmentModal's existing `submitting` pattern. Without
  // this the operator clicked Submit and saw the modal sit there for ~1-2s
  // with no feedback while the mutation ran — felt like the app hung.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const effectiveLocationId = selectedLocation?.id ?? targetLocationId;

  useEffect(() => {
    if (!isOpen) return;
    // When All Locations is selected, keep the last chosen location for convenience.
    setTargetLocationId((prev) => selectedLocation?.id ?? prev);
  }, [isOpen, selectedLocation?.id]);

  // Reset target org choice when the modal opens or the operative location
  // changes — picker is fresh-pick each time so muscle-memory misroutes
  // can't slip through (decision per CONTEXT.md / FE-5).
  useEffect(() => {
    if (!isOpen) return;
    setTargetOrgId(null);
  }, [isOpen, effectiveLocationId]);

  const locationProductsQuery = trpc.inventory.getProducts.useQuery(
    effectiveLocationId ? { locationId: effectiveLocationId } : {},
    {
      enabled: isOpen && !selectedLocation && !!effectiveLocationId,
      staleTime: 30_000,
    }
  );

  // Caller's own products (single-tenant or own-org-at-shared-location path).
  // Either prop-supplied (when a specific location is selected) or fetched
  // via getProducts (in "All Locations" mode where we re-resolve per location).
  const ownProducts: Product[] = useMemo(() => {
    if (selectedLocation) return products;
    const data = locationProductsQuery.data ?? [];
    return data.map((item) => ({
      id: item.id,
      sku: item.sku,
      title: item.title ?? undefined,
      category: {
        type: item.category?.type ?? undefined,
        size: item.category?.size ?? undefined,
        color: item.category?.color ?? undefined,
      },
      stockLevel: item.stockLevel,
      onHand: item.onHand ?? undefined,
      available: item.available ?? undefined,
      committed: item.committed ?? undefined,
      unavailable: item.unavailable ?? undefined,
      incoming: item.incoming ?? undefined,
      image_url: item.imageUrl ?? undefined,
      notify: item.notify ?? undefined,
      updated_at: item.updatedAt ?? undefined,
      skuCostOverride: item.skuCostOverride ?? null,
      effectiveCost: item.effectiveCost ?? undefined,
      isLocked: item.isLocked ?? undefined,
      productionType: ((item.productionType ?? 'POD') as 'PAP' | 'POD'),
      // BLU-669: MUST be carried through — filterToTargetOrg treats a missing
      // stamp as own-org (legacy fallback), so dropping it here would silently
      // re-admit connected clients' rows under an own-org target in the
      // All-Locations path.
      ownerOrganizationId: item.ownerOrganizationId ?? undefined,
    }));
  }, [selectedLocation, products, locationProductsQuery.data]);

  // BLU-3xx Phase 47.01.2-B-fix3: when the operator picks a connected
  // client in ClientTargetPicker, swap the SKU dropdown's data source to
  // that client's catalog. `useTargetCatalog` returns ownProducts for
  // own-org / single-tenant; only fetches cross-org when needed.
  const {
    options: selectableProducts,
    isLoading: isLoadingTargetCatalog,
    isEmpty: isTargetCatalogEmpty,
    isCrossOrg,
  } = useTargetCatalog({
    ownOrgId: organization?.id,
    targetOrgId,
    locationId: effectiveLocationId,
    ownProducts,
    isOpen,
  });

  // Clear SKU selection when switching locations in All Locations mode.
  useEffect(() => {
    if (selectedLocation) return;
    setSelectedSku("");
    setSearchQuery("");
  }, [selectedLocation, targetLocationId]);

  // BLU-3xx Phase 47.01.2-B-fix3: clear SKU + search when target org
  // changes — the SKU set is now scoped to the picked client. Don't carry
  // a stale own-org SKU into a client target.
  useEffect(() => {
    setSelectedSku("");
    setSearchQuery("");
  }, [targetOrgId]);

  // BLU-3xx Phase 47.01.2-B-fix3: focus-jump UX. When the operator picks
  // a client (gates the SKU dropdown), focus the SKU trigger so the next
  // keypress opens it. Compensates for the picker-first ordering — one
  // keystroke instead of one click. Refs the trigger element via
  // shadcn-radix's forwarded ref.
  const skuTriggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    if (!targetOrgId) return;
    // Defer to next tick so the now-enabled trigger is focusable.
    const id = window.setTimeout(() => {
      skuTriggerRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(id);
  }, [isOpen, targetOrgId]);

  // BLU-3xx Phase 47.01.2-B-04 + B-fix MED-6: shared
  // `useConnectedClients` hook so React Query dedupes the connected-
  // clients fetch across `useClientOrgFilter`, `ClientTargetPicker`,
  // and the modal-level "should we render the picker?" check below.
  const { clients: connectedClients } = useConnectedClients({
    locationId: effectiveLocationId,
    enabled:
      isOpen && SHARED_LOCATIONS_ENABLED && !!organization?.id,
  });

  const showPicker =
    SHARED_LOCATIONS_ENABLED &&
    !!organization?.id &&
    !!effectiveLocationId &&
    connectedClients.length > 0;

  const isOwnOrgTarget = !!targetOrgId && targetOrgId === organization?.id;

  // BLU-669: the own-org empty state's "Choose a client" CTA. Radix Select
  // TRAPS focus while open (FocusScope trapped + onFocusOutside prevented) and
  // restores focus to ITS OWN trigger on close — a plain focus() call from
  // inside SelectContent does nothing. So: controlled open state; the CTA
  // arms the redirect flag and closes; SelectContent's onCloseAutoFocus then
  // preventDefault()s the trigger re-focus and lands on the picker instead.
  const [skuSelectOpen, setSkuSelectOpen] = useState(false);
  const redirectFocusToPickerRef = useRef(false);

  // BLU-669: annotate the own-org picker option when the caller's own
  // (filtered) catalog at this location is empty — the operator learns the
  // dead end BEFORE committing two clicks to it, mirroring the shipped
  // ClientFilterDropdown "only offer what the data supports" precedent.
  const ownCatalogEmpty = useMemo(
    () =>
      !!organization?.id &&
      !locationProductsQuery.isFetching &&
      filterToTargetOrg(ownProducts, organization.id, organization.id).length === 0,
    [ownProducts, organization?.id, locationProductsQuery.isFetching],
  );

  // Resolved name for the sticky chip + the toast display label.
  // BLU-3xx Phase 47.01.2-B-fix2 NEW-FH1.4: i18n the (You) suffix
  // template + the "Your org" fallback so zh-CN users see localised
  // strings.
  const resolvedTargetName = useMemo(() => {
    if (!targetOrgId) return null;
    if (targetOrgId === organization?.id) {
      return organization?.name
        ? t('crossOrg.picker.youSuffix', { name: organization.name })
        : t('crossOrg.picker.yourOrgFallback');
    }
    const match = connectedClients.find((c) => c.id === targetOrgId);
    return match?.name ?? null;
  }, [targetOrgId, organization?.id, organization?.name, connectedClients, t]);

  const handleSubmit = async () => {
    if (!selectedSku || quantity <= 0 || !effectiveLocationId) return;
    if (showPicker && !targetOrgId) return; // gate enforced even if button somehow enabled
    if (isSubmitting) return;

    // BLU-3xx Phase 47.01.2-B-fix3.1: resolve from `selectableProducts`
    // (cross-org-aware via useTargetCatalog) — NOT from `products` prop,
    // which only contains own-org rows.
    const selectedProductForSubmit = selectableProducts.find(
      (p) => p.sku === selectedSku,
    );
    if (!selectedProductForSubmit) return;

    setIsSubmitting(true);
    try {
      const result = (await onAddStock(
        selectedProductForSubmit,
        quantity,
        date,
        effectiveLocationId,
        targetOrgId ?? undefined,
        reference.trim() || undefined,
      )) ?? { success: true };

      // Treat void / undefined as success for backwards compat with callers
      // that don't return a result.
      const normalised: AddStockResult =
        typeof result === 'object' && result !== null && 'success' in result
          ? result
          : { success: true };

      if (normalised.success) {
        const skuLabel = selectedProductForSubmit.sku;
        // BLU-3xx Phase 47.01.2-B-fix2 NEW-FH1.4: i18n the "for {{name}}"
        // connector via the `receiveUndo.targetSuffix` template so zh-CN
        // renders idiomatically. The displayLabel is then interpolated
        // into the bigger `receiveUndo.toast` template inside the hook.
        const targetSuffix = resolvedTargetName
          ? t('receiveUndo.targetSuffix', { name: resolvedTargetName })
          : '';
        const displayLabel = `${quantity} × ${skuLabel}${targetSuffix}`;

        // Show the 30-second Undo toast for ALL receives (own-org + cross-org)
        // per FE-5 — single behaviour > conditional behaviour.
        if (normalised.transactionId) {
          showReceiveUndo({
            transactionId: normalised.transactionId,
            displayLabel,
          });
        }
        resetForm();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedSku("");
    setQuantity(1);
    setDate(new Date());
    setReference("");
    setSearchQuery("");
    setTargetOrgId(null);
  };

  const filteredProducts = selectableProducts.filter((product) => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return true;
    const haystack = [
      product.sku,
      product.category?.type,
      product.category?.size,
      product.category?.color,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(normalizedQuery);
  });

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent shadcn Select from hijacking key presses while typing
    event.stopPropagation();
  };

  // BLU-3xx Phase 47.01.2-B-04: Cmd/Ctrl+Enter commits the form when picker
  // is filled (and other validation passes). Explicitly NOT when picker is
  // empty — keyboard shortcut respects the gate, doesn't bypass it.
  const submitDisabled =
    !selectedSku ||
    quantity <= 0 ||
    (!selectedLocation && !effectiveLocationId) ||
    (showPicker && !targetOrgId) ||
    isSubmitting;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      if (!submitDisabled) {
        event.preventDefault();
        void handleSubmit();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-[425px]"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle>{t('addStockModal.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('addStockModal.a11yDescription')}
          </DialogDescription>
          {/* BLU-3xx Phase 47.01.2-B-04: sticky context chip — persists
              across form steps; NOT dismissible mid-form (provenance, not
              a control). Re-pick by changing the dropdown directly. */}
          {targetOrgId && resolvedTargetName && (
            <div className="pt-1">
              <ClientTargetChip
                resolvedName={resolvedTargetName}
                label={t('crossOrg.picker.labelReceiving')}
                isOwnOrg={isOwnOrgTarget}
              />
            </div>
          )}
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {!selectedLocation && (
            <div className="grid gap-2">
              <Label>{t('addStockModal.locationLabel')}</Label>
              <Select value={targetLocationId} onValueChange={setTargetLocationId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('addStockModal.selectLocation')} />
                </SelectTrigger>
                <SelectContent>
                  <StockWriteLocationItems locations={allLocations} />
                </SelectContent>
              </Select>
            </div>
          )}
          {/* BLU-3xx Phase 47.01.2-B-04 + B-fix3: required cross-org target
              picker. Renders ONLY at locations with connected clients.
              Reordered above SKU so the picker is the first decision the
              operator makes; the SKU dropdown is gated until this is set.
              Subtle bordered group signals "this is the gating decision". */}
          {showPicker && organization && (
            <div className="rounded-md border border-border/60 bg-muted/20 p-3">
              <ClientTargetPicker
                value={targetOrgId}
                onChange={setTargetOrgId}
                ownOrgId={organization.id}
                ownOrgName={organization.name ?? t('crossOrg.picker.yourOrgFallback')}
                locationId={effectiveLocationId ?? ''}
                required
                ownOrgSubtext={
                  ownCatalogEmpty ? t('crossOrg.picker.ownOrgNoCatalog') : undefined
                }
              />
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="sku">{t('addStockModal.skuLabel')}</Label>
            <Select
              value={selectedSku}
              onValueChange={setSelectedSku}
              open={skuSelectOpen}
              onOpenChange={setSkuSelectOpen}
            >
              <SelectTrigger
                ref={skuTriggerRef}
                disabled={
                  (!selectedLocation && !effectiveLocationId) ||
                  (showPicker && !targetOrgId)
                }
                className="transition-colors duration-200"
              >
                {showPicker && !targetOrgId ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Lock className="h-4 w-4" />
                    {t('crossOrg.modal.skuPickClientFirst')}
                  </span>
                ) : (
                  <SelectValue placeholder={t('addStockModal.selectProduct')} />
                )}
              </SelectTrigger>
              <SelectContent
                onCloseAutoFocus={(event) => {
                  // BLU-669: when the empty-state CTA closed the dropdown,
                  // suppress Radix's default focus-return-to-trigger and land
                  // on the client target picker instead.
                  if (redirectFocusToPickerRef.current) {
                    redirectFocusToPickerRef.current = false;
                    event.preventDefault();
                    document.getElementById('client-target-picker')?.focus();
                  }
                }}
              >
                <div className="p-2">
                  <Input
                    placeholder={t('addStockModal.searchProducts')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="mb-2"
                    onKeyDown={handleSearchKeyDown}
                  />
                </div>
                {isLoadingTargetCatalog ? (
                  <>
                    {[0, 1, 2].map((i) => (
                      <Skeleton key={`sk-${i}`} className="mx-2 mb-1 h-9" />
                    ))}
                  </>
                ) : isTargetCatalogEmpty && isOwnOrgTarget && !locationProductsQuery.isFetching ? (
                  // BLU-669: own-org target at a shared location where the
                  // caller holds no catalog (the common 3PL-owner shape —
                  // Thread Lab owns zero SKUs at Ningbo). Previously this
                  // state was unreachable (isEmpty was cross-org-only) and,
                  // once the own-org filter landed, would have rendered a
                  // silent blank list. CTA lands focus on the target picker —
                  // choosing a client is the recovery, not /products.
                  <div className="p-3 text-center">
                    <p className="text-sm text-muted-foreground">
                      {t('crossOrg.modal.skuEmptyOwnCatalog', {
                        name: organization?.name ?? t('crossOrg.picker.yourOrgFallback'),
                      })}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('crossOrg.modal.skuEmptyOwnCatalogHelp')}
                    </p>
                    <Button
                      variant="link"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        // Radix Select traps focus while open — a bare
                        // focus() from in here is a no-op. Arm the redirect
                        // and close; onCloseAutoFocus does the landing.
                        redirectFocusToPickerRef.current = true;
                        setSkuSelectOpen(false);
                      }}
                    >
                      {t('crossOrg.modal.skuEmptyOwnCatalogCta')}
                    </Button>
                  </div>
                ) : isCrossOrg && isTargetCatalogEmpty && resolvedTargetName ? (
                  <div className="p-3 text-center">
                    <p className="text-sm text-muted-foreground">
                      {t('crossOrg.modal.skuEmptyCatalog', {
                        client: resolvedTargetName,
                      })}
                    </p>
                    {/* BLU-669: the old CTA linked /products?targetOrg=… — a
                        dead end (Products never reads the param; createSku
                        has no cross-org target) that would have minted an
                        OWN-org SKU where a client one was intended. A client
                        catalog is populated by the client's product sync, so
                        the honest affordance is the explanation alone. */}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('crossOrg.modal.skuEmptyCatalogHelp', {
                        client: resolvedTargetName,
                      })}
                    </p>
                  </div>
                ) : (
                  filteredProducts.map((product) => (
                    <SelectItem key={product.sku} value={product.sku} className="group">
                      <div className="flex flex-col">
                        <span className="group-data-[state=checked]:text-accent-foreground group-data-[highlighted]:text-accent-foreground">
                          {product.sku}
                        </span>
                        <span className="text-xs text-muted-foreground group-data-[state=checked]:text-accent-foreground group-data-[highlighted]:text-accent-foreground">
                          {joinParts([product.category.type, product.category.size], ' - ')}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {!selectedLocation && !effectiveLocationId && (
              <p className="text-xs text-muted-foreground">{t('addStockModal.chooseLocationFirst')}</p>
            )}
            {!selectedLocation && effectiveLocationId && locationProductsQuery.isFetching && (
              <p className="text-xs text-muted-foreground">{t('addStockModal.loadingProducts')}</p>
            )}
            {/* Helper text intentionally omitted when picker is unset — the
                in-trigger label + Lock icon already say "Pick a client first"
                and the trigger is announced via aria-describedby on the
                picker. Removing the duplicate paragraph (post-review). */}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="quantity">{t('addStockModal.quantityLabel')}</Label>
            <Input
              id="quantity"
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
            />
          </div>
          <AdjustmentReferenceField
            id="add-stock-reference"
            value={reference}
            onChange={setReference}
          />
          <div className="grid gap-2">
            <Label htmlFor="date">{t('addStockModal.dateLabel')}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="date"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : <span>{t('addStockModal.pickDate')}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(date) => date && setDate(date)}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('addStockModal.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitDisabled}
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
            aria-keyshortcuts="Meta+Enter"
            aria-busy={isSubmitting || undefined}
          >
            {isSubmitting && (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
            )}
            {t('addStockModal.addStock')}
            {/* BLU-3xx Phase 47.01.2-B-fix MED-8: surface the
                Cmd/Ctrl+Enter shortcut on the primary CTA so operators
                discover the keyboard shortcut without reading the
                tooltip. Mirrors `CurrencyTogglePill.tsx`. Hidden during
                submit so the spinner has the visual focus. */}
            {!isSubmitting && (
              <kbd className="ml-1 rounded border border-border/60 bg-white/10 px-1 text-[10px] font-mono">
                ⌘↵
              </kbd>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddStockModal;
